import React from 'react';
import { ChartType, VisibleScenarios } from '../../types';
import { Card } from '../ui';
import { WealthChart } from './WealthChart';
import { IncomeChart } from './IncomeChart';

interface MainChartProps {
  chartType: ChartType;
  onChartTypeChange: (type: ChartType) => void;
  visibleScenarios: VisibleScenarios;
  onScenarioToggle: (scenario: keyof VisibleScenarios) => void;
  chartData: any[];
  formatCurrency: (value: number, compact?: boolean) => string;
}


export const MainChart: React.FC<MainChartProps> = ({
  chartType,
  onChartTypeChange,
  visibleScenarios,
  onScenarioToggle,
  chartData,
  formatCurrency
}) => {
  return (
    <Card padding="lg">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
        <div>
          <h3 className="text-base font-medium text-gray-900 mb-1">
            {chartType === 'total' ? '10-Year Wealth Projection' : 'Annual Income & Expenses'}
          </h3>
          <p className="text-sm text-gray-500">Real dollars (inflation-adjusted)</p>
        </div>
        <div className="flex items-center gap-3 mt-3 lg:mt-0">
          <span className="text-sm text-gray-500">View:</span>
          <div className="inline-flex border border-gray-300">
            <button
              onClick={() => onChartTypeChange('total')}
              className={`px-3 py-1 text-sm ${chartType === 'total' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              Wealth
            </button>
            <button
              onClick={() => onChartTypeChange('annual')}
              className={`px-3 py-1 text-sm border-l border-gray-300 ${chartType === 'annual' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              Income
            </button>
          </div>
        </div>
      </div>

      {/* Scenario Toggle - custom checkboxes */}
      <div className="flex flex-wrap gap-5 mb-6 text-sm">
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <span className={`w-3.5 h-3.5 border flex items-center justify-center ${visibleScenarios.rent ? 'bg-gray-900 border-gray-900' : 'border-gray-400'}`}>
            {visibleScenarios.rent && <span className="text-white text-[10px]">✓</span>}
          </span>
          <input type="checkbox" checked={visibleScenarios.rent} onChange={() => onScenarioToggle('rent')} className="sr-only" />
          <span className={visibleScenarios.rent ? 'text-gray-900' : 'text-gray-400'}>Keep & Rent</span>
        </label>

        <label className="flex items-center gap-2 cursor-pointer select-none">
          <span className={`w-3.5 h-3.5 border flex items-center justify-center ${visibleScenarios.sell100 ? 'bg-gray-900 border-gray-900' : 'border-gray-400'}`}>
            {visibleScenarios.sell100 && <span className="text-white text-[10px]">✓</span>}
          </span>
          <input type="checkbox" checked={visibleScenarios.sell100} onChange={() => onScenarioToggle('sell100')} className="sr-only" />
          <span className={visibleScenarios.sell100 ? 'text-gray-900' : 'text-gray-400'}>100% Stocks</span>
        </label>

        <label className="flex items-center gap-2 cursor-pointer select-none">
          <span className={`w-3.5 h-3.5 border flex items-center justify-center ${visibleScenarios.sell80_20 ? 'bg-gray-900 border-gray-900' : 'border-gray-400'}`}>
            {visibleScenarios.sell80_20 && <span className="text-white text-[10px]">✓</span>}
          </span>
          <input type="checkbox" checked={visibleScenarios.sell80_20} onChange={() => onScenarioToggle('sell80_20')} className="sr-only" />
          <span className={visibleScenarios.sell80_20 ? 'text-gray-900' : 'text-gray-400'}>80/20 Mix</span>
        </label>

        <label className="flex items-center gap-2 cursor-pointer select-none">
          <span className={`w-3.5 h-3.5 border flex items-center justify-center ${visibleScenarios.sell60_40 ? 'bg-gray-900 border-gray-900' : 'border-gray-400'}`}>
            {visibleScenarios.sell60_40 && <span className="text-white text-[10px]">✓</span>}
          </span>
          <input type="checkbox" checked={visibleScenarios.sell60_40} onChange={() => onScenarioToggle('sell60_40')} className="sr-only" />
          <span className={visibleScenarios.sell60_40 ? 'text-gray-900' : 'text-gray-400'}>60/40 Mix</span>
        </label>
      </div>

      <div className="h-72">
        {chartType === 'total' ? (
          <WealthChart
            data={chartData}
            visibleScenarios={visibleScenarios}
            formatCurrency={formatCurrency}
          />
        ) : (
          <IncomeChart
            data={chartData}
            formatCurrency={formatCurrency}
          />
        )}
      </div>
    </Card>
  );
};
